import { useCallback } from 'react'
import { useAtomValue } from 'jotai'
import { styled } from '@mui/material/styles'
import { useTranslate } from '@repo/unicity'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { omanFormatDataAtom, omanDeliveryFreightAtom } from '../../../stores/oman.store'
import { omanConfig } from '../../../oman.config'
import { useAvailableShippingMethods, formatOmanShippingLabel } from '../../../utils/oman-shipping-methods'
import type { TOmanShippingMethodId } from '../../../types/oman.types'
import type { RadioProps } from '@mui/material/Radio'

const getRadioInputTestProps = (testId: string) =>
    ({
        'data-testid': testId
    }) as NonNullable<RadioProps['slotProps']>['input']

interface OmanShippingMethodProps {
    freight?: number
    disabled?: boolean
    onMethodChange?: (method: TOmanShippingMethodId) => void | Promise<boolean | void>
}

const OmanShippingMethod = ({ freight: freightProp, disabled = false, onMethodChange }: OmanShippingMethodProps) => {
    const { translate } = useTranslate()
    const formatData = useAtomValue(omanFormatDataAtom)
    const deliveryFreight = useAtomValue(omanDeliveryFreightAtom)
    const freight = freightProp ?? (deliveryFreight || formatData?.freight)

    const handleAutoSwitch = useCallback(
        (next: TOmanShippingMethodId) => {
            void onMethodChange?.(next)
        },
        [onMethodChange]
    )
    const { availableMethods, selectedMethod, setSelectedMethod } = useAvailableShippingMethods({
        onAutoSwitch: handleAutoSwitch
    })

    const handleSelect = (method: TOmanShippingMethodId) => {
        if (disabled || method === selectedMethod) return
        setSelectedMethod(method)
        void onMethodChange?.(method)
    }

    return (
        <FormControl
            component="fieldset"
            fullWidth
            sx={{
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                transition: 'opacity 0.2s'
            }}
            data-testid="oman-shipping-method-form-control">
            <RadioGroup
                value={selectedMethod}
                onChange={e => handleSelect(e.target.value as TOmanShippingMethodId)}
                data-testid="oman-shipping-method-radio-group">
                {availableMethods.map(method => {
                    const label = formatOmanShippingLabel({
                        id: method.id,
                        baseLabel: translate(method.labelKey),
                        freight,
                        freeLabel: translate('free')
                    })
                    return (
                        <MethodCard
                            key={method.id}
                            selected={selectedMethod === method.id}
                            onClick={() => handleSelect(method.id)}
                            data-testid={`oman-shipping-method-${method.id}-method-card`}>
                            <FormControlLabel
                                value={method.id}
                                control={
                                    <Radio
                                        size="small"
                                        sx={{ alignSelf: 'flex-start', p: 0, mr: 2 }}
                                        slotProps={{
                                            input: getRadioInputTestProps(`oman-shipping-method-${method.id}-input`)
                                        }}
                                        data-testid={`oman-shipping-method-${method.id}-radio`}
                                    />
                                }
                                label={
                                    <Stack spacing={0} data-testid={`oman-shipping-method-${method.id}-label-stack`}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: 14,
                                                fontWeight: 400,
                                                color: selectedMethod === method.id ? '#003764' : 'rgb(102, 135, 162)'
                                            }}
                                            data-testid={`oman-shipping-method-${method.id}-label`}>
                                            {label}
                                        </Typography>
                                        {method.id === 'pickup' && selectedMethod === 'pickup' && (
                                            <Stack spacing={0} sx={{ mt: 0.5 }} data-testid="oman-shipping-method-pickup-address-stack">
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontSize: 14, fontWeight: 400, color: '#003764', lineHeight: 1.6 }}
                                                    data-testid="oman-shipping-method-pickup-name">
                                                    {omanConfig.pickup.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontSize: 14, fontWeight: 400, color: '#003764', lineHeight: 1.6 }}
                                                    data-testid="oman-shipping-method-pickup-address">
                                                    {omanConfig.pickup.address}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontSize: 14, fontWeight: 400, color: '#003764', lineHeight: 1.6 }}
                                                    data-testid="oman-shipping-method-pickup-city">
                                                    {omanConfig.pickup.postalCode} {omanConfig.pickup.city}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontSize: 14, fontWeight: 400, color: '#003764', lineHeight: 1.6 }}
                                                    data-testid="oman-shipping-method-pickup-country">
                                                    {omanConfig.pickup.country}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Stack>
                                }
                                sx={{
                                    alignItems: 'flex-start',
                                    m: 0,
                                    width: '100%'
                                }}
                                data-testid={`oman-shipping-method-${method.id}-form-control-label`}
                            />
                        </MethodCard>
                    )
                })}
            </RadioGroup>
        </FormControl>
    )
}

const MethodCard = styled(Box, {
    shouldForwardProp: p => p !== 'selected'
})<{ selected: boolean }>`
    padding: 20px 0;
    cursor: pointer;

    & + & {
        border-top: 1px solid rgb(229, 238, 247);
    }
`

export default OmanShippingMethod
